<template>
    <div class="ag-filter-body" style="padding: 8px;">
        <!-- Text input and Add button side by side -->
        <div class="ag-filter-form" style="display: flex; gap: 8px; align-items: center;">
            <div class="ag-input-wrapper" style="flex: 1;">
                <input class="ag-input-field-input ag-text-field-input" v-model="inputValue" ref="inputRef"
                @keyup.enter="addValue" @input="formatInput" placeholder="Enter value" maxlength="7" style="width: 100%;" />
            </div>
            <button class="ag-button ag-standard-button" type="button" @click="addValue" :disabled="!isInputValid">
                Add
            </button>
        </div>

        <!-- Filter value list -->
        <div class="ag-filter-list"
            style="margin-top: 12px; max-height: 120px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px;">
            <div v-for="(val, index) in values" :key="index" class="ag-filter-list-item"
                style="display: flex; justify-content: space-between; align-items: center; padding: 4px 6px; background-color: var(--ag-background-color, #f4f4f4); border-radius: 4px;">
                <span style="flex: 1;">{{ val }}</span>
                <button class="ag-button ag-filter-remove-button" @click="removeValue(index)"
                    style="margin-left: 8px; padding: 0 6px;">
                    ✕
                </button>
            </div>
        </div>

        <!-- Apply button -->
        <div class="ag-filter-apply-panel" style="margin-top: 12px;">
            <button class="ag-button ag-standard-button" type="button" @click="applyFilter">
                Apply
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IDoesFilterPassParams, IFilterParams } from 'ag-grid-enterprise';
import { computed, nextTick, ref } from 'vue';

// Props
const props = defineProps<{ params: IFilterParams }>();

// Data
const inputValue = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const values = ref<string[]>([]);

const isInputValid = computed(() => {
  const regex = /^[A-Z]{3}-[A-Z]{3}$/;
  return regex.test(inputValue.value);
});

// Events
function addValue() {
    const val = inputValue.value.trim();
    if (val && !values.value.includes(val)) {
        values.value.push(val);
        inputValue.value = '';
    }
}

function removeValue(index: number) {
    values.value.splice(index, 1);
}

function applyFilter() {
    if (props.params) {
        props.params.filterChangedCallback();
    }
}

function isFilterActive() {
    return values.value.length > 0;
}

// Functions used by AG Grid
function doesFilterPass(rowParams: IDoesFilterPassParams) {
  const cellValue = props.params.getValue(rowParams.node);
  return values.value.includes(String(cellValue ?? '').toUpperCase());
}

function getModel() {
    return isFilterActive() ? { values: values.value } : null;
}

function setModel(model: { values?: string[] } | null) {
  values.value = model?.values ?? [];
}

function afterGuiAttached() {
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus();
    }
  });
}

function formatInput(event: InputEvent) {
  let raw = inputValue.value.toUpperCase().replace(/[^A-Z]/g, '');

  if (event.inputType === 'deleteContentBackward') {
    if (raw.length <= 3) {
      inputValue.value = raw;
    } else {
      inputValue.value = raw.slice(0, 3) + '-' + raw.slice(3, 6);
    }
    return;
  }

  if (raw.length >= 3) {
    raw = raw.slice(0, 3) + '-' + raw.slice(3, 6);
  }

  inputValue.value = raw.slice(0, 7);
}

defineExpose({
  isFilterActive,
  doesFilterPass,
  getModel,
  setModel,
  afterGuiAttached,
});
</script>

<style scoped>
.ag-input-wrapper {
    display: flex;
}

.ag-button {
    font-size: 12px;
    line-height: 1.4;
}

.ag-filter-remove-button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ag-secondary-foreground-color, #333);
}
</style>
